// The piece the AI carries. The AI driver lifts it, releases it when the move
// is played, and ends the carry when the piece is at rest. The board reads the
// pose on every frame. A carry is presentation: the game state does not know
// about it.

import {
  BEAT_PACE,
  CARRY_CAPTURE_PORTION,
  CARRY_DROP_PORTION,
  REDUCED_MOTION_QUERY,
} from '../constants';
import { createStore } from '../lib/store';
import { type CarryPlan, type CarryPose, carryPose, planCarry } from '../motion/carry';
import { isBoardFlipped } from './localSide';

export type Carry = {
  pieceId: number;
  plan: CarryPlan;
  /** Clock times, from `performance.now`. */
  liftedAt: number;
  /** Null while the piece hangs over its first square. */
  movedAt: number | null;
};

export const carryStore = createStore<Carry | null>(null);

/**
 * Lifts a piece off its square. Returns the plan, or null with reduced
 * motion: then the piece does not come up and the move is a plain change of
 * square.
 */
export const liftPiece = (pieceId: number, from: number, to: number): CarryPlan | null => {
  if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return null;
  const plan = planCarry(from, to, Math.random, isBoardFlipped());
  carryStore.set({ pieceId, plan, liftedAt: performance.now(), movedAt: null });
  return plan;
};

/** Starts the travel. Call it with the move, so the piece leaves as the board changes. */
export const releasePiece = (): void => {
  const carry = carryStore.get();
  if (carry) carryStore.set({ ...carry, movedAt: performance.now() });
};

export const endCarry = (): void => {
  if (carryStore.get() !== null) carryStore.set(null);
};

/** The plan counts beat time. The clock counts real time. */
const beatTime = (realMs: number): number => realMs / BEAT_PACE;

export const poseOfCarry = (carry: Carry, now: number): CarryPose =>
  carryPose(
    carry.plan,
    beatTime(now - carry.liftedAt),
    carry.movedAt === null ? null : beatTime(now - carry.movedAt),
  );

/** How long a captured piece waits before it fades, so the carried piece is almost on it. */
export const captureDelayMs = (carry: Carry, now: number): number => {
  if (carry.movedAt === null) return 0;
  const fadeAt = carry.movedAt + carry.plan.travelMs * CARRY_CAPTURE_PORTION * BEAT_PACE;
  return Math.max(0, Math.round(fadeAt - now));
};

/** How long until the carried piece is down on its square. Zero when it is not released. */
export const landDelayMs = (carry: Carry, now: number): number => {
  if (carry.movedAt === null) return 0;
  const { travelMs, landMs } = carry.plan;
  const downAt = carry.movedAt + (travelMs + landMs * CARRY_DROP_PORTION) * BEAT_PACE;
  return Math.max(0, Math.round(downAt - now));
};
